import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, PencilIcon, TrashIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { environment } from './environment';

function getToken() {
  return localStorage.getItem("token");
}

const ModalityDetail = () => {
  const { modalityId } = useParams();
  const navigate = useNavigate();
  
  const [modality, setModality] = useState(null);
  const [modalitySteps, setModalitySteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newStepName, setNewStepName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedStep, setSelectedStep] = useState(null);
  const [editStepName, setEditStepName] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState('');
  
  useEffect(() => {
    fetchModality();
    fetchModalitySteps();
  }, [modalityId]);

  const fetchModality = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${environment.apiUrl}/modalities/${modalityId}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setModality(data);
      } else {
        setError('Failed to fetch modality');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  const fetchModalitySteps = async () => {
    try {
      const response = await fetch(`${environment.apiUrl}/modality-steps/`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        // Filter steps that belong to this modality
        const filteredSteps = data.filter(step => step.modality_id === modalityId);
        setModalitySteps(filteredSteps);
      }
    } catch (err) {
      console.error('Error fetching modality steps:', err);
    }
  };

  const handleAddStep = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');

    try {
      const steps = newStepName.split('\n').filter(step => step.trim() !== '');
      
      for (const stepName of steps) {
        const response = await fetch(`${environment.apiUrl}/modality-steps/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify({
            modality_id: modalityId,
            name: stepName.trim()
          }),
        });

        if (!response.ok) {
          const data = await response.json();
          setSubmitError(data.detail || 'Failed to create modality step');
          return;
        }
      }

      setShowAddModal(false);
      setNewStepName('');
      fetchModalitySteps();
    } catch (err) {
      setSubmitError('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditStep = (step) => {
    setSelectedStep(step);
    setEditStepName(step.name);
    setEditError('');
    setShowEditModal(true);
  };

  const handleUpdateStep = async (e) => {
    e.preventDefault();
    setEditSubmitting(true);
    setEditError('');

    try {
      const response = await fetch(`${environment.apiUrl}/modality-steps/${selectedStep.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          modality_id: modalityId,
          name: editStepName.trim()
        }),
      });

      if (response.ok) {
        setShowEditModal(false);
        setSelectedStep(null);
        setEditStepName('');
        fetchModalitySteps();
      } else {
        const data = await response.json();
        setEditError(data.detail || 'Failed to update modality step');
      }
    } catch (err) {
      setEditError('Network error');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDeleteStep = (step) => {
    setSelectedStep(step);
    setShowDeleteModal(true);
  };

  const confirmDeleteStep = async () => {
    if (!selectedStep) return;
    
    try {
      const response = await fetch(`${environment.apiUrl}/modality-steps/${selectedStep.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.ok) {
        setShowDeleteModal(false);
        setSelectedStep(null);
        fetchModalitySteps();
      } else {
        setError('Failed to delete modality step');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setShowDeleteModal(false);
      setSelectedStep(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !modality) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
          {error || 'Modality not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/settings?tab=modality')}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5 text-slate-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">{modality.name}</h1>
                <p className="text-sm text-slate-500">Modality Details</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/settings?tab=modality')}
              className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              Back to Settings
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Modality Info */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-lg p-6 border border-slate-200 sticky top-24">
              <h2 className="text-lg font-semibold text-slate-900 mb-6">Modality Information</h2>
              
              <div className="space-y-6">
                <div>
                  <label className="text-sm font-medium text-slate-500 block mb-2">Name</label>
                  <p className="text-base text-slate-900 font-semibold">{modality.name}</p>
                </div>

                {modality.short_term && (
                  <div>
                    <label className="text-sm font-medium text-slate-500 block mb-2">Short Term</label>
                    <p className="text-base text-slate-700">{modality.short_term}</p>
                  </div>
                )}

                {modality.modality_setting && (
                  <div>
                    <label className="text-sm font-medium text-slate-500 block mb-2">Setting</label>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                      {modality.modality_setting}
                    </span>
                  </div>
                )}

                {modality.description && (
                  <div>
                    <label className="text-sm font-medium text-slate-500 block mb-2">Description</label>
                    <p className="text-base text-slate-700 whitespace-pre-wrap leading-relaxed">
                      {modality.description}
                    </p>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-slate-500 block mb-2">Created</label>
                  <p className="text-sm text-slate-600">
                    {new Date(modality.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Modality Steps */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-lg p-6 border border-slate-200">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-slate-900">
                  Modality Steps ({modalitySteps.length})
                </h2>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                >
                  <PlusIcon className="w-4 h-4" />
                  Add Modality Step
                </button>
              </div>

              {modalitySteps.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <p className="text-slate-600 font-medium mb-2">No modality steps yet</p>
                  <p className="text-sm text-slate-500">Add steps to define the process for this modality</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {modalitySteps.map((step, index) => (
                    <div
                      key={step.id}
                      className="flex items-start gap-4 p-4 bg-gradient-to-r from-slate-50 to-white border border-slate-200 rounded-lg hover:shadow-md transition-all duration-200"
                    >
                      <div className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-base text-slate-900 font-medium break-words">
                          {step.name}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          Created {new Date(step.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEditStep(step)}
                          className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all duration-200 hover:shadow-sm"
                          title="Edit modality step"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteStep(step)}
                          className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200 hover:shadow-sm"
                          title="Delete modality step"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Step Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-slate-900">Add Modality Step</h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewStepName('');
                  setSubmitError('');
                }}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5 text-slate-600" />
              </button>
            </div>

            <form onSubmit={handleAddStep}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Step Name(s)
                </label>
                <textarea
                  value={newStepName}
                  onChange={(e) => setNewStepName(e.target.value)}
                  placeholder="Enter step name (one per line for multiple steps)"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                  rows={5}
                  required
                />
                <p className="text-xs text-slate-500 mt-2">
                  Tip: Enter multiple steps, one per line, to create them all at once
                </p>
              </div>

              {submitError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {submitError}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setNewStepName('');
                    setSubmitError('');
                  }}
                  className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium disabled:bg-purple-300 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Adding...' : 'Add Modality Step'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Step Modal */}
      {showEditModal && selectedStep && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-slate-900">Edit Modality Step</h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedStep(null);
                  setEditStepName('');
                  setEditError('');
                }}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5 text-slate-600" />
              </button>
            </div>

            <form onSubmit={handleUpdateStep}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Step Name
                </label>
                <input
                  type="text"
                  value={editStepName}
                  onChange={(e) => setEditStepName(e.target.value)}
                  placeholder="Enter step name"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  required
                />
              </div>

              {editError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {editError}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedStep(null);
                    setEditStepName('');
                    setEditError('');
                  }}
                  className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editSubmitting}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium disabled:bg-purple-300 disabled:cursor-not-allowed"
                >
                  {editSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Step Modal */}
      {showDeleteModal && selectedStep && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 text-center">
            <h3 className="text-xl font-bold text-red-700 mb-4">Delete Modality Step</h3>
            <p className="mb-6 text-slate-700">
              Are you sure you want to delete <span className="font-semibold">{selectedStep.name}</span>? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={confirmDeleteStep}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Delete
              </button>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedStep(null);
                }}
                className="px-6 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModalityDetail;
